import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Save, X, AlertCircle, Calculator, History, GripVertical, Eye, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost, apiPut } from '../../lib/api';

const INP = "w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-[#3b4872] focus:border-[#3b4872]";

const KIND_LABELS = {
  percent: '% of basis',
  flat: 'Flat amount',
  conditional_flat: 'Conditional flat',
  balancer: 'Balancer (auto)',
};

function nextMonthStr() {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// Part B: the PF wording is built from the flagged components ("Basic + DA"), never hard-coded.
function sortedDraftLabel(comps) {
  const parts = [...(comps || [])]
    .filter(c => c.active && c.pf_wages)
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map(c => (String(c.code).toUpperCase() === 'BASIC' ? 'Basic' : c.code));
  return parts.length ? parts.join(' + ') : '—';
}

export default function SalaryStructureMaster() {
  const [draft, setDraft] = useState([]);
  const [rules, setRules] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [previewGross, setPreviewGross] = useState(25000);
  const [previewOptOut, setPreviewOptOut] = useState(false);
  const [preview, setPreview] = useState(null);
  // Round 2 #4: new preview inputs
  const [previewMonth, setPreviewMonth] = useState(() => nextMonthStr());
  const [previewGender, setPreviewGender] = useState('');
  const [previewPostingType, setPreviewPostingType] = useState('');
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [viewingVersion, setViewingVersion] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [dragCode, setDragCode] = useState(null);
  const [dragOverCode, setDragOverCode] = useState(null);
  const [baselineJson, setBaselineJson] = useState('[]');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [comps, r, v] = await Promise.all([
        apiGet('/hr/salary-master/components'),
        apiGet('/hr/salary-master/pf-rules'),
        apiGet('/hr/salary-master/versions'),
      ]);
      setDraft(comps);
      setBaselineJson(JSON.stringify(comps));
      setRules(r);
      setVersions(v);
    } catch {
      toast.error('Failed to load Salary Master');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); }, []);

  const doPreview = async () => {
    try {
      const r = await apiPost('/hr/salary-master/preview', {
        gross: parseFloat(previewGross) || 0,
        opt_out_pf: previewOptOut,
        month: previewMonth || undefined,
        gender: previewGender || undefined,
        posting_type: previewPostingType || undefined,
      });
      setPreview(r);
    } catch {
      toast.error('Preview failed');
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { doPreview(); }, [previewGross, previewOptOut, previewMonth, previewGender, previewPostingType]);

  const currentVersion = versions.find(v => v.is_currently_effective);
  const latestVersion = versions[0];

  const upsertDraft = (c) => {
    setDraft(prev => (prev.some(x => x.id === c.id) ? prev.map(x => (x.id === c.id ? c : x)) : [...prev, c]));
    setEditing(null);
  };

  const deleteFromDraft = (c) => {
    if (c.is_system) { toast.error('System components cannot be removed — uncheck Active to deactivate instead.'); return; }
    if (!window.confirm(`Remove ${c.code} from the draft? This only takes effect once you save a new version.`)) return;
    setDraft(prev => prev.filter(x => x.id !== c.id));
  };

  const toggleActive = (c) => setDraft(prev => prev.map(x => (x.id === c.id ? { ...x, active: !x.active } : x)));
  const togglePfWages = (c) => setDraft(prev => prev.map(x => (x.id === c.id ? { ...x, pf_wages: !x.pf_wages } : x)));
  const pfWageLabel = sortedDraftLabel(draft);

  const saveRules = async () => {
    try {
      await apiPut('/hr/salary-master/pf-rules', rules);
      toast.success('PF/ESI rules saved');
      doPreview();
    } catch {
      toast.error('Save failed');
    }
  };

  // Client-side mirror of the backend's save-time % validation — informational
  // only, so the admin sees the ceiling coming before they hit Save New Version.
  const referenceGross = Number(rules?.validation_reference_gross || 100000);
  const basicRow = draft.find(c => c.code === 'BASIC');
  const basicPctOfGross = basicRow ? Number(basicRow.value || 0) : 0;
  const pctOfGross = (c) => {
    if (c.kind === 'balancer') return 0;
    if (c.kind === 'percent') return c.basis === 'basic' ? (Number(c.value || 0) / 100) * basicPctOfGross : Number(c.value || 0);
    if (c.kind === 'flat') return referenceGross ? (Number(c.value || 0) / referenceGross) * 100 : 0;
    if (c.kind === 'conditional_flat') {
      const worst = Math.max(Number(c.value || 0), Number(c.conditional?.else_value || 0));
      return referenceGross ? (worst / referenceGross) * 100 : 0;
    }
    return 0;
  };
  const totalPct = draft.filter(c => c.active).reduce((s, c) => s + pctOfGross(c), 0);
  const isOverBudget = totalPct > 100.01;
  const isDraftDirty = JSON.stringify(draft) !== baselineJson;

  const sortedDraft = [...draft].sort((a, b) => (a.order || 0) - (b.order || 0));

  const handleDragStart = (id) => (e) => {
    setDragCode(id);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', id); } catch { /* noop */ }
  };
  const handleDragOver = (id) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverCode !== id) setDragOverCode(id);
  };
  const handleDrop = (targetId) => (e) => {
    e.preventDefault();
    if (!dragCode || dragCode === targetId) { setDragCode(null); setDragOverCode(null); return; }
    const ids = sortedDraft.map(c => c.id);
    const from = ids.indexOf(dragCode);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) { setDragCode(null); setDragOverCode(null); return; }
    const next = [...ids];
    next.splice(from, 1);
    next.splice(to, 0, dragCode);
    setDraft(prev => {
      const byId = new Map(prev.map(c => [c.id, c]));
      return next.map((id, i) => ({ ...byId.get(id), order: i + 1 }));
    });
    setDragCode(null); setDragOverCode(null);
  };
  const handleDragEnd = () => { setDragCode(null); setDragOverCode(null); };

  if (loading) return <div className="p-8 text-center text-sm text-slate-500">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="salary-master-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">Masters · HR</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Salary Structure Master</h1>
          <p className="text-sm text-slate-600 mt-1.5 max-w-2xl">
            Define how monthly gross salary breaks into components. Every save creates a new dated
            version — nothing here mutates a past month's payroll silently.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowHistory(true)} className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 hover:bg-slate-50 rounded-sm text-xs font-semibold" data-testid="version-history-btn">
            <History size={14} /> Version History
          </button>
          <button onClick={fetchAll} className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 hover:bg-slate-50 rounded-sm text-xs font-semibold" data-testid="discard-draft-btn">
            <RotateCcw size={14} /> Discard Draft
          </button>
        </div>
      </div>

      {currentVersion && latestVersion && (
        <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-sm px-4 py-2" data-testid="version-status-banner">
          Editing draft based on <strong>version {latestVersion.version_no}</strong> (effective {latestVersion.effective_from}).
          {' '}Currently effective for payroll: <strong>version {currentVersion.version_no}</strong> (effective {currentVersion.effective_from}).
          {latestVersion.version_no !== currentVersion.version_no && (
            <span className="text-amber-700"> This draft is dated for the future and won't apply until then.</span>
          )}
        </div>
      )}

      {/* Components Table */}
      <div className="border border-slate-200 rounded-sm bg-white">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-display font-bold text-base">Components</h2>
          <div className="flex items-center gap-3">
            <span className={`text-xs font-mono font-bold ${isOverBudget ? 'text-red-600' : 'text-slate-500'}`} data-testid="total-pct-of-gross">
              Total ≈ {totalPct.toFixed(2)}% of gross
            </span>
            <button
              onClick={() => setEditing({ code: '', name: '', kind: 'percent', basis: 'gross', value: 0, order: draft.length + 1, active: true, id: crypto.randomUUID() })}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#3b4872] hover:bg-[#2c3658] text-white rounded-sm text-xs font-semibold"
              data-testid="add-component-btn"
            >
              <Plus size={14} /> Add Component
            </button>
            <button
              onClick={() => setSaveModalOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs font-semibold"
              data-testid="save-new-version-btn"
            >
              <Save size={14} /> Save New Version
            </button>
          </div>
        </div>
        {isOverBudget && (
          <div className="px-4 py-2 bg-red-50 border-b border-red-200 text-xs text-red-700 flex items-center gap-2" data-testid="over-budget-warning">
            <AlertCircle size={14} className="shrink-0" />
            {isDraftDirty ? (
              <span>This draft totals more than 100% of gross — saving will be rejected until it's corrected.</span>
            ) : (
              <span>The currently saved version totals ≈{totalPct.toFixed(2)}% of gross — it was migrated into version 1 without validation, so payroll still runs on it. Your next save must bring the total to ≤100% in a single save; incremental saves will be rejected.</span>
            )}
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase font-bold tracking-wider text-slate-500">
            <tr>
              <th className="px-2 py-2 w-6"></th>
              <th className="px-4 py-2 text-left">Code</th>
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Kind</th>
              <th className="px-4 py-2 text-left">Basis / Value</th>
              <th className="px-4 py-2 text-right">% of Gross</th>
              <th className="px-4 py-2 text-center" title="Counts towards PF wages (Basic + DA)">PF wages</th>
              <th className="px-4 py-2 text-center">Active</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {sortedDraft.map(c => (
              <tr
                key={c.id}
                draggable
                onDragStart={handleDragStart(c.id)}
                onDragOver={handleDragOver(c.id)}
                onDragLeave={() => setDragOverCode(null)}
                onDrop={handleDrop(c.id)}
                onDragEnd={handleDragEnd}
                className={`border-t border-slate-100 hover:bg-slate-50 ${dragCode === c.id ? 'opacity-40' : ''} ${dragOverCode === c.id && dragCode && dragCode !== c.id ? 'border-t-2 border-t-[#3b4872]' : ''} ${!c.active ? 'opacity-50' : ''}`}
                data-testid={`component-row-${c.code}`}
              >
                <td className="px-2 py-2.5 text-slate-300 cursor-grab" title="Drag to reorder"><GripVertical size={14} /></td>
                <td className="px-4 py-2.5 font-mono font-bold text-[#3b4872]">{c.code}{c.is_system && <span className="ml-1.5 text-[9px] font-bold text-slate-400 uppercase">system</span>}</td>
                <td className="px-4 py-2.5">{c.name}</td>
                <td className="px-4 py-2.5 text-xs">{KIND_LABELS[c.kind]}</td>
                <td className="px-4 py-2.5 text-xs">
                  {c.kind === 'percent' && <>{c.value}% of {c.basis}</>}
                  {c.kind === 'flat' && <>₹{Number(c.value).toLocaleString()}</>}
                  {c.kind === 'conditional_flat' && <>₹{Number(c.value).toLocaleString()}</>}
                  {c.kind === 'balancer' && <span className="text-slate-400">auto</span>}
                </td>
                <td className="px-4 py-2.5 text-right font-mono text-xs text-slate-500">{c.active ? `${pctOfGross(c).toFixed(2)}%` : '—'}</td>
                <td className="px-4 py-2.5 text-center">
                  <input type="checkbox" checked={!!c.pf_wages} onChange={() => togglePfWages(c)} className="rounded" data-testid={`toggle-pf-wages-${c.code}`} />
                </td>
                <td className="px-4 py-2.5 text-center">
                  <input type="checkbox" checked={c.active} onChange={() => toggleActive(c)} className="rounded" data-testid={`toggle-active-${c.code}`} />
                </td>
                <td className="px-4 py-2.5 text-right">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => setEditing(c)} className="p-1.5 hover:bg-slate-200 rounded-sm" data-testid={`edit-${c.code}`}>
                      <Edit2 size={13} />
                    </button>
                    {!c.is_system && (
                      <button onClick={() => deleteFromDraft(c)} className="p-1.5 hover:bg-red-100 text-red-600 rounded-sm" data-testid={`delete-${c.code}`}>
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-4 py-2 border-t border-slate-100 text-xs text-slate-600" data-testid="pf-wages-summary">
          <span className="font-semibold text-[#3b4872]">PF wages = {pfWageLabel}</span>
          <span className="text-slate-400"> · 12% of this sum, capped at the ceiling in force for the month (₹15,000 before 17-Sep-2026, ₹25,000 from then)</span>
        </div>
      </div>

      {/* ESI & PT Rules — Round 2 #4: replaced dead singleton card with versioned tables */}
      <ESIRuleVersionsPanel />
      <PTRuleVersionsPanel />

      {/* Validation reference gross — the only surviving field from the old singleton */}
      <div className="border border-slate-200 rounded-sm bg-white p-4 space-y-3">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display font-bold text-base">Validation Settings</h2>
          <button onClick={saveRules} className="px-3 py-1.5 bg-[#3b4872] hover:bg-[#2c3658] text-white rounded-sm text-xs font-semibold flex items-center gap-1.5" data-testid="save-pf-rules">
            <Save size={13} /> Save
          </button>
        </div>
        {rules && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <Field label="Reference gross for % validation (Rs.)">
              <input type="number" value={rules.validation_reference_gross} onChange={e => setRules({ ...rules, validation_reference_gross: +e.target.value })} className={INP} data-testid="validation-reference-gross" />
              <p className="text-[10px] text-slate-500 mt-1">Used only to check that components don't exceed 100% of gross at save time.</p>
            </Field>
          </div>
        )}
      </div>

      {/* PF Rule Versions — effective-dated ceiling + brackets (PF Engine §B) */}
      <PFRuleVersionsPanel />

      {/* Live preview — Round 2 #4: add Gender, Posting type, Month inputs */}
      <div className={`border rounded-sm p-4 ${preview?.over_allocated ? 'border-red-300 bg-red-50/40' : 'border-indigo-200 bg-indigo-50/40'}`}>
        <div className="flex items-center gap-2 mb-3">
          <Calculator size={16} className="text-[#3b4872]" />
          <h2 className="font-display font-bold text-base">Live Preview</h2>
          <span className="text-[11px] text-slate-500">— effective version v{preview?.structure_version_no ?? currentVersion?.version_no ?? '—'}</span>
        </div>
        {preview?.over_allocated && (
          <div className="mb-3 px-3 py-2 bg-red-100 border border-red-300 rounded-sm text-xs text-red-800 flex items-center gap-2" data-testid="preview-over-allocated-warning">
            <AlertCircle size={14} /> Components sum to {preview.over_allocated_pct}% — exceeds 100%. Needs correcting.
          </div>
        )}
        <div className="grid md:grid-cols-3 gap-4">
          <div className="space-y-3">
            <Field label="Test gross salary (₹)">
              <input type="number" value={previewGross} onChange={e => setPreviewGross(e.target.value)} className={INP} data-testid="preview-gross" />
            </Field>
            <Field label="Month">
              <input type="month" value={previewMonth} onChange={e => setPreviewMonth(e.target.value)} className={INP} data-testid="preview-month" />
            </Field>
            <Field label="Gender">
              <select value={previewGender} onChange={e => setPreviewGender(e.target.value)} className={INP} data-testid="preview-gender">
                <option value="">— Any —</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </Field>
            <Field label="Posting type">
              <select value={previewPostingType} onChange={e => setPreviewPostingType(e.target.value)} className={INP} data-testid="preview-posting-type">
                <option value="">— Any —</option>
                <option value="office">Office (ESIC)</option>
                <option value="site">Site (Workmen's Comp.)</option>
              </select>
            </Field>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={previewOptOut} onChange={e => setPreviewOptOut(e.target.checked)} className="rounded" />
              Opted out of PF (Form 11)
            </label>
          </div>
          <div className="md:col-span-2">
            {preview && (
              <div className="bg-white border border-slate-200 rounded-sm p-3 text-sm">
                <div className="space-y-1.5">
                  {preview.components.map(c => (
                    <div key={c.code} className="flex justify-between">
                      <span className="text-slate-600">{c.name} ({c.code})</span>
                      <span className="font-mono">₹{c.amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                    </div>
                  ))}
                  <div className="flex justify-between border-t border-slate-200 pt-1.5 mt-1.5 font-bold">
                    <span>Gross</span>
                    <span className="font-mono">₹{preview.gross.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                  </div>
                  {preview.pf_inclusion_mode !== 'both_above' && (
                    <div className="flex justify-between text-red-600 text-xs">
                      <span>PF (employee) {preview.deductions.opt_out_pf ? '(opted out)' : `— ${preview.deductions.pf_wage_label || 'Basic+DA'}`}</span>
                      <span className="font-mono">₹{(preview.deductions.employee_pf || 0).toLocaleString('en-IN')}</span>
                    </div>
                  )}
                  {preview.pf_inclusion_mode === 'both_within' && (
                    <div className="flex justify-between text-red-600 text-xs">
                      <span>PF – Employer share (CTC-inclusive)</span>
                      <span className="font-mono">₹{(preview.deductions.employer_pf || 0).toLocaleString('en-IN')}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-red-600 text-xs">
                    <span>ESI {preview.coverage_type === 'ESIC' ? `(employee · ${preview.deductions.esi_wage_label || 'Basic'})` : `— ${preview.coverage_type || 'N/A'}`}</span>
                    <span className="font-mono">₹{(preview.deductions.employee_esi || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-red-600 text-xs">
                    <span>Professional Tax</span>
                    <span className="font-mono">₹{(preview.deductions.professional_tax || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between border-t border-slate-200 pt-1.5 mt-1.5 font-bold text-emerald-700">
                    <span>Net take-home</span>
                    <span className="font-mono">₹{preview.net.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="text-[10px] text-slate-400 pt-1">
                    PF bracket: {preview.pf_inclusion_mode}
                    {preview.pf_inclusion_mode === 'both_above' && ' — both PFs paid by employer above gross'}
                    {preview.pf_inclusion_mode === 'employee_within' && ' — employee PF deducted, employer PF is on top'}
                    {preview.pf_inclusion_mode === 'both_within' && ' — both PFs CTC-inclusive'}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {editing && <ComponentModal initial={editing} onClose={() => setEditing(null)} onSave={upsertDraft} />}
      {saveModalOpen && (
        <SaveVersionModal
          draft={draft}
          defaultMonth={nextMonthStr()}
          onClose={() => setSaveModalOpen(false)}
          onSaved={() => { setSaveModalOpen(false); fetchAll(); }}
        />
      )}
      {showHistory && <VersionHistoryModal versions={versions} onClose={() => setShowHistory(false)} onView={(no) => setViewingVersion(no)} />}
      {viewingVersion != null && <VersionDetailModal versionNo={viewingVersion} onClose={() => setViewingVersion(null)} />}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

function ComponentModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState({
    ...initial,
    conditional: initial.conditional || { threshold: 0, else_value: 0 },
  });
  const u = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isNew = !initial.code;

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-md max-w-lg w-full shadow-xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display font-bold">{isNew ? 'Add' : 'Edit'} Component (draft)</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Code *">
              <input
                value={form.code}
                disabled={form.is_system}
                onChange={e => u('code', e.target.value.toUpperCase())}
                className={INP + (form.is_system ? ' bg-slate-100 text-slate-500' : '')}
                placeholder="BASIC, DA, ..."
                data-testid="comp-code"
              />
            </Field>
            <Field label="Order">
              <input type="number" value={form.order} onChange={e => u('order', +e.target.value)} className={INP} />
            </Field>
          </div>
          <Field label="Name *">
            <input value={form.name} onChange={e => u('name', e.target.value)} className={INP} placeholder="Basic Pay, House Rent Allowance, ..." data-testid="comp-name" />
          </Field>
          <Field label="Kind *">
            <select value={form.kind} onChange={e => u('kind', e.target.value)} className={INP} data-testid="comp-kind">
              <option value="percent">% of basis</option>
              <option value="flat">Flat amount</option>
              <option value="conditional_flat">Conditional flat amount</option>
              <option value="balancer">Balancer (auto-fills the gap)</option>
            </select>
          </Field>
          {form.kind === 'percent' && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="% of *">
                <select value={form.basis || 'gross'} onChange={e => u('basis', e.target.value)} className={INP}>
                  <option value="gross">Gross</option>
                  <option value="basic">Basic</option>
                </select>
              </Field>
              <Field label="Percent *">
                <input type="number" step="0.01" value={form.value} onChange={e => u('value', +e.target.value)} className={INP} placeholder="50" />
              </Field>
            </div>
          )}
          {(form.kind === 'flat' || form.kind === 'conditional_flat') && (
            <Field label="Amount (₹) *">
              <input type="number" step="0.01" value={form.value} onChange={e => u('value', +e.target.value)} className={INP} />
            </Field>
          )}
          {form.kind === 'conditional_flat' && (
            <div className="grid grid-cols-2 gap-3 p-3 bg-amber-50 rounded-sm border border-amber-200">
              <Field label="Apply ONLY if gross >">
                <input type="number" value={form.conditional.threshold} onChange={e => u('conditional', { ...form.conditional, threshold: +e.target.value })} className={INP} />
              </Field>
              <Field label="Else use amount (₹)">
                <input type="number" value={form.conditional.else_value} onChange={e => u('conditional', { ...form.conditional, else_value: +e.target.value })} className={INP} />
              </Field>
            </div>
          )}
          {form.kind === 'balancer' && (
            <div className="flex gap-2 items-start p-3 bg-indigo-50 rounded-sm border border-indigo-200 text-xs text-slate-700">
              <AlertCircle size={14} className="mt-0.5 text-[#3b4872]" />
              <span>Balancer fills the gap so component total = gross. Only ONE active balancer is allowed.</span>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm pt-2">
            <input type="checkbox" checked={form.active} onChange={e => u('active', e.target.checked)} className="rounded" />
            Active
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.pf_wages} onChange={e => u('pf_wages', e.target.checked)} className="rounded" data-testid="comp-pf-wages" />
            Counts towards PF wages (Basic + DA rule)
          </label>
        </div>
        <div className="border-t border-slate-200 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-slate-300 rounded-sm text-sm">Cancel</button>
          <button onClick={() => onSave(form)} className="px-4 py-2 bg-[#3b4872] hover:bg-[#2c3658] text-white rounded-sm text-sm font-semibold flex items-center gap-1.5" data-testid="save-component">
            <Save size={14} /> Save to Draft
          </button>
        </div>
      </div>
    </div>
  );
}

function SaveVersionModal({ draft, defaultMonth, onClose, onSaved }) {
  const [effectiveFrom, setEffectiveFrom] = useState(defaultMonth);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const res = await apiPost('/hr/salary-master/save-version', {
        components: draft.map(({ id, code, name, kind, basis, value, conditional, order, active, pf_wages }) => ({ id, code, name, kind, basis, value, conditional, order, active, pf_wages: !!pf_wages })),
        effective_from: effectiveFrom,
        note: note || undefined,
      });
      toast.success(`Version ${res.version_no} saved, effective ${res.effective_from}`);
      if (res.affected_completed_months?.length) {
        toast.warning(`${res.affected_completed_months.length} already-run month(s) will use this version if re-run: ${res.affected_completed_months.join(', ')}`, { duration: 9000 });
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-md max-w-md w-full shadow-xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display font-bold">Save New Version</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Effective from (month) *">
            <input type="month" value={effectiveFrom} onChange={e => setEffectiveFrom(e.target.value)} className={INP} data-testid="save-version-effective-from" />
          </Field>
          <p className="text-xs text-slate-500">
            Defaults to next month. Choosing a month that's already had payroll run will list those
            months so you know a re-run would pick up this version — it won't block the save.
          </p>
          <Field label="Note (optional)">
            <textarea value={note} onChange={e => setNote(e.target.value)} className={INP} rows={2} placeholder="Why is this changing?" data-testid="save-version-note" />
          </Field>
        </div>
        <div className="border-t border-slate-200 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-slate-300 rounded-sm text-sm">Cancel</button>
          <button onClick={submit} disabled={saving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-sm font-semibold flex items-center gap-1.5" data-testid="confirm-save-version-btn">
            <Save size={14} /> {saving ? 'Saving…' : 'Save Version'}
          </button>
        </div>
      </div>
    </div>
  );
}

function VersionHistoryModal({ versions, onClose, onView }) {
  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-md max-w-2xl w-full shadow-xl" data-testid="version-history-modal">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display font-bold">Version History</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 max-h-[70vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase font-bold tracking-wider text-slate-500">
              <tr>
                <th className="text-left py-1.5">Version</th>
                <th className="text-left py-1.5">Effective From</th>
                <th className="text-left py-1.5">Created By</th>
                <th className="text-left py-1.5">Note</th>
                <th className="text-right py-1.5"></th>
              </tr>
            </thead>
            <tbody>
              {versions.map(v => (
                <tr key={v.version_no} className="border-t border-slate-100" data-testid={`version-row-${v.version_no}`}>
                  <td className="py-2 font-mono font-bold text-[#3b4872]">
                    v{v.version_no} {v.is_currently_effective && <span className="ml-1.5 text-[9px] font-bold text-emerald-600 uppercase">effective now</span>}
                  </td>
                  <td className="py-2">{v.effective_from}</td>
                  <td className="py-2 text-xs text-slate-600">{v.created_by}</td>
                  <td className="py-2 text-xs text-slate-500">{v.note || '—'}</td>
                  <td className="py-2 text-right">
                    <button onClick={() => onView(v.version_no)} className="text-[#3b4872] hover:underline text-xs flex items-center gap-1 ml-auto" data-testid={`view-version-${v.version_no}`}>
                      <Eye size={12} /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function VersionDetailModal({ versionNo, onClose }) {
  const [version, setVersion] = useState(null);
  useEffect(() => {
    apiGet(`/hr/salary-master/versions/${versionNo}`).then(setVersion).catch(() => toast.error('Failed to load version'));
  }, [versionNo]);

  return (
    <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-md max-w-2xl w-full shadow-xl" data-testid="version-detail-modal">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display font-bold">Version {versionNo} (read-only)</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 max-h-[70vh] overflow-y-auto">
          {!version ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase font-bold tracking-wider text-slate-500">
                <tr>
                  <th className="text-left py-1.5">Code</th>
                  <th className="text-left py-1.5">Name</th>
                  <th className="text-left py-1.5">Kind</th>
                  <th className="text-left py-1.5">Basis / Value</th>
                  <th className="text-center py-1.5">PF wages</th>
                  <th className="text-center py-1.5">Active</th>
                </tr>
              </thead>
              <tbody>
                {[...(version.components || [])].sort((a, b) => (a.order || 0) - (b.order || 0)).map(c => (
                  <tr key={c.id} className="border-t border-slate-100">
                    <td className="py-2 font-mono font-bold text-[#3b4872]">{c.code}</td>
                    <td className="py-2">{c.name}</td>
                    <td className="py-2 text-xs">{KIND_LABELS[c.kind]}</td>
                    <td className="py-2 text-xs">
                      {c.kind === 'percent' && <>{c.value}% of {c.basis}</>}
                      {(c.kind === 'flat' || c.kind === 'conditional_flat') && <>₹{Number(c.value).toLocaleString()}</>}
                      {c.kind === 'balancer' && <span className="text-slate-400">auto</span>}
                    </td>
                    <td className="py-2 text-center">{c.pf_wages ? '✓' : '—'}</td>
                    <td className="py-2 text-center">{c.active ? '✓' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}


// PF Rule Versions Panel — PF_Engine_WageCeiling_25000_26Sep2026 §B
function PFRuleVersionsPanel() {
  const [versions, setVersions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showNew, setShowNew] = React.useState(false);
  const [form, setForm] = React.useState({
    effective_from: '', pf_wage_ceiling: 25000, ee_pct: 12, er_pct: 12,
    eps_pct: 8.33, eps_enabled: true, edli_pct: 0.5, admin_pct: 0.5,
    admin_min_monthly: 500, note: '',
    // Round 3 #4: bracket fields — pre-fill from current version in load()
    inclusion_both_above_below: 15000, inclusion_employee_within_upto: 25000, inclusion_low_inclusive: true,
  });
  const [saving, setSaving] = React.useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await apiGet('/hr/salary-master/pf-rule-versions');
      setVersions(d.versions || []);
      // Round 3 #4: pre-fill bracket fields from the current (newest) version
      if (d.versions && d.versions.length > 0) {
        const current = d.versions[0];
        setForm(f => ({
          ...f,
          pf_wage_ceiling: current.pf_wage_ceiling || 25000,
          inclusion_both_above_below: current.inclusion_both_above_below || 15000,
          inclusion_employee_within_upto: current.inclusion_employee_within_upto || 25000,
          inclusion_low_inclusive: current.inclusion_low_inclusive !== undefined ? current.inclusion_low_inclusive : true,
          ee_pct: current.ee_pct || 12,
          er_pct: current.er_pct || 12,
          eps_pct: current.eps_pct || 8.33,
          edli_pct: current.edli_pct || 0.5,
          admin_pct: current.admin_pct || 0.5,
        }));
      }
    } catch { toast.error('Failed to load PF rule versions'); }
    finally { setLoading(false); }
  };
  React.useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.effective_from) { toast.error('Effective from date is required'); return; }
    setSaving(true);
    try {
      await apiPost('/hr/salary-master/pf-rule-versions', form);
      toast.success('PF rule version created');
      setShowNew(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-slate-200 rounded-sm bg-white p-4 space-y-3" data-testid="pf-rule-versions-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-bold text-base">PF Rule Versions</h2>
          <p className="text-xs text-slate-500 mt-0.5">Effective-dated wage ceiling, EPS/EDLI rates — drives all payroll runs from each date.</p>
        </div>
        <button onClick={() => setShowNew(!showNew)} className="px-3 py-1.5 bg-[#3b4872] hover:bg-[#2c3658] text-white rounded-sm text-xs font-semibold flex items-center gap-1.5" data-testid="add-pf-version-btn">
          <Plus size={13} /> New Version
        </button>
      </div>
      {loading ? <p className="text-xs text-slate-400">Loading…</p> : (
        <table className="w-full text-xs border-collapse" data-testid="pf-versions-table">
          <thead>
            <tr className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px]">
              <th className="text-left px-3 py-2">Effective From</th>
              <th className="text-right px-3 py-2">Wage Ceiling</th>
              <th className="text-right px-3 py-2">Low bracket up to</th>
              <th className="text-right px-3 py-2">Mid bracket up to</th>
              <th className="text-center px-3 py-2">Low inclusive</th>
              <th className="text-right px-3 py-2">EE %</th>
              <th className="text-right px-3 py-2">ER %</th>
              <th className="text-right px-3 py-2">EPS %</th>
              <th className="text-right px-3 py-2">EDLI %</th>
              <th className="text-right px-3 py-2">Admin %</th>
              <th className="text-left px-3 py-2">Created By</th>
            </tr>
          </thead>
          <tbody>
            {versions.map((v, i) => (
              <tr key={v.id} className={`border-t border-slate-100 ${v.effective_from <= new Date().toISOString().slice(0,10) && (i === 0 || versions[i-1]?.effective_from > new Date().toISOString().slice(0,10)) ? 'bg-emerald-50/50' : ''}`} data-testid={`pf-version-row-${v.effective_from}`}>
                <td className="px-3 py-2 font-semibold">{v.effective_from}{v.effective_from <= new Date().toISOString().slice(0,10) && (i === 0 || versions[i-1]?.effective_from > new Date().toISOString().slice(0,10)) && <span className="ml-2 text-emerald-700 text-[10px] font-bold">CURRENT</span>}</td>
                <td className="px-3 py-2 text-right font-mono">Rs.{Number(v.pf_wage_ceiling).toLocaleString('en-IN')}</td>
                <td className="px-3 py-2 text-right font-mono">₹{Number(v.inclusion_both_above_below || 15000).toLocaleString('en-IN')}</td>
                <td className="px-3 py-2 text-right font-mono">₹{Number(v.inclusion_employee_within_upto || 21000).toLocaleString('en-IN')}</td>
                <td className="px-3 py-2 text-center">{v.inclusion_low_inclusive ? '≤' : '<'}</td>
                <td className="px-3 py-2 text-right">{v.ee_pct}%</td>
                <td className="px-3 py-2 text-right">{v.er_pct}%</td>
                <td className="px-3 py-2 text-right">{v.eps_pct}%</td>
                <td className="px-3 py-2 text-right">{v.edli_pct}%</td>
                <td className="px-3 py-2 text-right">{v.admin_pct}%</td>
                <td className="px-3 py-2 text-slate-500">{v.created_by}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {showNew && (
        <div className="border border-indigo-200 rounded-sm bg-indigo-50/30 p-4 space-y-3" data-testid="new-pf-version-form">
          <h3 className="text-sm font-semibold">New PF Rule Version</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Field label="Effective From *">
              <input type="date" value={form.effective_from} onChange={e => setForm({...form, effective_from: e.target.value})} className={INP} data-testid="pf-version-effective-from" />
            </Field>
            <Field label="Wage Ceiling (Rs.)">
              <input type="number" value={form.pf_wage_ceiling} onChange={e => setForm({...form, pf_wage_ceiling: +e.target.value})} className={INP} data-testid="pf-version-ceiling" />
            </Field>
            <Field label="Low bracket up to (₹)" >
              <input type="number" value={form.inclusion_both_above_below} onChange={e => setForm({...form, inclusion_both_above_below: +e.target.value})} className={INP} data-testid="pf-version-low-bracket" />
              <p className="text-[10px] text-slate-500 mt-0.5">Gross ≤ this → employer pays both PFs</p>
            </Field>
            <Field label="Middle bracket up to (₹)">
              <input type="number" value={form.inclusion_employee_within_upto} onChange={e => setForm({...form, inclusion_employee_within_upto: +e.target.value})} className={INP} data-testid="pf-version-mid-bracket" />
              <p className="text-[10px] text-slate-500 mt-0.5">Gross ≤ this → only employee PF deducted</p>
            </Field>
            <Field label="Low bracket inclusive (≤ vs <)">
              <label className="flex items-center gap-2 mt-1 text-sm cursor-pointer">
                <input type="checkbox" checked={!!form.inclusion_low_inclusive} onChange={e => setForm({...form, inclusion_low_inclusive: e.target.checked})} data-testid="pf-version-low-inclusive" />
                Low bracket uses ≤ (inclusive)
              </label>
              <p className="text-[10px] text-slate-500 mt-0.5">Oct 2026+ uses ≤ so ₹15,000 gross → both_above</p>
            </Field>
            <Field label="EE %"><input type="number" step="0.01" value={form.ee_pct} onChange={e => setForm({...form, ee_pct: +e.target.value})} className={INP} /></Field>
            <Field label="ER %"><input type="number" step="0.01" value={form.er_pct} onChange={e => setForm({...form, er_pct: +e.target.value})} className={INP} /></Field>
            <Field label="EPS %"><input type="number" step="0.01" value={form.eps_pct} onChange={e => setForm({...form, eps_pct: +e.target.value})} className={INP} /></Field>
            <Field label="EDLI %"><input type="number" step="0.01" value={form.edli_pct} onChange={e => setForm({...form, edli_pct: +e.target.value})} className={INP} /></Field>
            <Field label="Admin %"><input type="number" step="0.01" value={form.admin_pct} onChange={e => setForm({...form, admin_pct: +e.target.value})} className={INP} /></Field>
            <Field label="Admin Min (Rs.)"><input type="number" value={form.admin_min_monthly} onChange={e => setForm({...form, admin_min_monthly: +e.target.value})} className={INP} /></Field>
          </div>
          <Field label="Note">
            <input value={form.note} onChange={e => setForm({...form, note: e.target.value})} placeholder="e.g. Gazette S.O. 5109(E) of 17-Sep-2026" className={INP} />
          </Field>
          <div className="flex gap-2">
            <button onClick={save} disabled={saving} className="px-4 py-1.5 bg-[#3b4872] text-white rounded-sm text-xs font-semibold" data-testid="save-pf-version-btn">
              {saving ? 'Saving…' : 'Create Version'}
            </button>
            <button onClick={() => setShowNew(false)} className="px-4 py-1.5 border border-slate-300 rounded-sm text-xs">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}


// HR_Payroll_Changes Round 2 #4 — ESI Rule Versions Panel
function ESIRuleVersionsPanel() {
  const [versions, setVersions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showNew, setShowNew] = React.useState(false);
  const [form, setForm] = React.useState({ effective_from: '', wage_base_mode: 'basic_only', esi_wage_ceiling: 21000, ee_pct: 0.75, er_pct: 3.25, note: '' });
  const [saving, setSaving] = React.useState(false);
  const load = async () => {
    setLoading(true);
    try { const d = await apiGet('/hr/salary-master/esi-rule-versions'); setVersions(d.versions || []); }
    catch { toast.error('Failed to load ESI rule versions'); }
    finally { setLoading(false); }
  };
  React.useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.effective_from) { toast.error('Effective from date is required'); return; }
    setSaving(true);
    try { await apiPost('/hr/salary-master/esi-rule-versions', form); toast.success('ESI rule version created'); setShowNew(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="border border-slate-200 rounded-sm bg-white p-4 space-y-3" data-testid="esi-rule-versions-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-bold text-base">ESI Rule Versions</h2>
          <p className="text-xs text-slate-500 mt-0.5">Effective-dated ESI wage base mode, ceiling and rates.</p>
        </div>
        <button onClick={() => setShowNew(!showNew)} className="px-3 py-1.5 bg-[#3b4872] hover:bg-[#2c3658] text-white rounded-sm text-xs font-semibold flex items-center gap-1.5" data-testid="add-esi-version-btn">
          <Plus size={13} /> New Version
        </button>
      </div>
      {loading ? <p className="text-xs text-slate-400">Loading…</p> : (
        <table className="w-full text-xs border-collapse" data-testid="esi-versions-table">
          <thead>
            <tr className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px]">
              <th className="text-left px-3 py-2">Effective From</th>
              <th className="text-left px-3 py-2">Wage Base</th>
              <th className="text-right px-3 py-2">Ceiling</th>
              <th className="text-right px-3 py-2">EE %</th>
              <th className="text-right px-3 py-2">ER %</th>
              <th className="text-left px-3 py-2">Created By</th>
            </tr>
          </thead>
          <tbody>
            {versions.map((v, i) => (
              <tr key={v.id} className={`border-t border-slate-100 ${v.effective_from <= today && (i === 0 || versions[i-1]?.effective_from > today) ? 'bg-emerald-50/50' : ''}`} data-testid={`esi-version-row-${v.effective_from}`}>
                <td className="px-3 py-2 font-semibold">{v.effective_from}{v.effective_from <= today && (i === 0 || versions[i-1]?.effective_from > today) && <span className="ml-2 text-emerald-700 text-[10px] font-bold">CURRENT</span>}</td>
                <td className="px-3 py-2">{v.wage_base_mode === 'basic_only' ? 'Basic Pay only' : 'Gross − HRA − CA − MA'}</td>
                <td className="px-3 py-2 text-right font-mono">₹{Number(v.esi_wage_ceiling).toLocaleString('en-IN')}</td>
                <td className="px-3 py-2 text-right">{v.ee_pct}%</td>
                <td className="px-3 py-2 text-right">{v.er_pct}%</td>
                <td className="px-3 py-2 text-slate-500">{v.created_by}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {showNew && (
        <div className="border border-indigo-200 rounded-sm bg-indigo-50/30 p-4 space-y-3" data-testid="new-esi-version-form">
          <h3 className="text-sm font-semibold">New ESI Rule Version</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <Field label="Effective From *">
              <input type="date" value={form.effective_from} onChange={e => setForm({...form, effective_from: e.target.value})} className={INP} data-testid="esi-version-effective-from" />
            </Field>
            <Field label="Wage Base Mode">
              <select value={form.wage_base_mode} onChange={e => setForm({...form, wage_base_mode: e.target.value})} className={INP}>
                <option value="basic_only">Basic Pay only</option>
                <option value="gross_minus_hra_ca_ma">Gross − HRA − CA − MA</option>
              </select>
            </Field>
            <Field label="Ceiling (Rs.)"><input type="number" value={form.esi_wage_ceiling} onChange={e => setForm({...form, esi_wage_ceiling: +e.target.value})} className={INP} /></Field>
            <Field label="EE %"><input type="number" step="0.01" value={form.ee_pct} onChange={e => setForm({...form, ee_pct: +e.target.value})} className={INP} /></Field>
            <Field label="ER %"><input type="number" step="0.01" value={form.er_pct} onChange={e => setForm({...form, er_pct: +e.target.value})} className={INP} /></Field>
          </div>
          <Field label="Note"><input value={form.note} onChange={e => setForm({...form, note: e.target.value})} className={INP} /></Field>
          <div className="flex gap-2">
            <button onClick={save} disabled={saving} className="px-4 py-1.5 bg-[#3b4872] text-white rounded-sm text-xs font-semibold" data-testid="save-esi-version-btn">
              {saving ? 'Saving…' : 'Create Version'}
            </button>
            <button onClick={() => setShowNew(false)} className="px-4 py-1.5 border border-slate-300 rounded-sm text-xs">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// HR_Payroll_Changes Round 2 #4 — PT Rule Versions Panel
function PTRuleVersionsPanel() {
  const [versions, setVersions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showNew, setShowNew] = React.useState(false);
  const [form, setForm] = React.useState({ effective_from: '', gender_slabs_enabled: false, flat_amount: 200, february_topup_amount: 300, note: '' });
  const [saving, setSaving] = React.useState(false);
  const load = async () => {
    setLoading(true);
    try { const d = await apiGet('/hr/salary-master/pt-rule-versions'); setVersions(d.versions || []); }
    catch { toast.error('Failed to load PT rule versions'); }
    finally { setLoading(false); }
  };
  React.useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.effective_from) { toast.error('Effective from date is required'); return; }
    setSaving(true);
    try { await apiPost('/hr/salary-master/pt-rule-versions', form); toast.success('PT rule version created'); setShowNew(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="border border-slate-200 rounded-sm bg-white p-4 space-y-3" data-testid="pt-rule-versions-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-bold text-base">PT Rule Versions</h2>
          <p className="text-xs text-slate-500 mt-0.5">Effective-dated Professional Tax rules (flat amount or gender slabs).</p>
        </div>
        <button onClick={() => setShowNew(!showNew)} className="px-3 py-1.5 bg-[#3b4872] hover:bg-[#2c3658] text-white rounded-sm text-xs font-semibold flex items-center gap-1.5" data-testid="add-pt-version-btn">
          <Plus size={13} /> New Version
        </button>
      </div>
      {loading ? <p className="text-xs text-slate-400">Loading…</p> : (
        <table className="w-full text-xs border-collapse" data-testid="pt-versions-table">
          <thead>
            <tr className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px]">
              <th className="text-left px-3 py-2">Effective From</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-right px-3 py-2">Monthly Amount</th>
              <th className="text-right px-3 py-2">Feb Amount</th>
              <th className="text-left px-3 py-2">Created By</th>
            </tr>
          </thead>
          <tbody>
            {versions.map((v, i) => (
              <tr key={v.id} className={`border-t border-slate-100 ${v.effective_from <= today && (i === 0 || versions[i-1]?.effective_from > today) ? 'bg-emerald-50/50' : ''}`} data-testid={`pt-version-row-${v.effective_from}`}>
                <td className="px-3 py-2 font-semibold">{v.effective_from}{v.effective_from <= today && (i === 0 || versions[i-1]?.effective_from > today) && <span className="ml-2 text-emerald-700 text-[10px] font-bold">CURRENT</span>}</td>
                <td className="px-3 py-2">{v.gender_slabs_enabled ? 'Gender slabs' : 'Flat'}</td>
                <td className="px-3 py-2 text-right font-mono">₹{v.flat_amount || '—'}</td>
                <td className="px-3 py-2 text-right font-mono">₹{v.february_topup_amount || '—'}</td>
                <td className="px-3 py-2 text-slate-500">{v.created_by}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {showNew && (
        <div className="border border-indigo-200 rounded-sm bg-indigo-50/30 p-4 space-y-3" data-testid="new-pt-version-form">
          <h3 className="text-sm font-semibold">New PT Rule Version</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <Field label="Effective From *">
              <input type="date" value={form.effective_from} onChange={e => setForm({...form, effective_from: e.target.value})} className={INP} data-testid="pt-version-effective-from" />
            </Field>
            <Field label="Type">
              <select value={form.gender_slabs_enabled ? 'slabs' : 'flat'} onChange={e => setForm({...form, gender_slabs_enabled: e.target.value === 'slabs'})} className={INP}>
                <option value="flat">Flat amount</option>
                <option value="slabs">Gender slabs</option>
              </select>
            </Field>
            {!form.gender_slabs_enabled && (
              <>
                <Field label="Monthly Amount (Rs.)"><input type="number" value={form.flat_amount} onChange={e => setForm({...form, flat_amount: +e.target.value})} className={INP} /></Field>
                <Field label="February Amount (Rs.)"><input type="number" value={form.february_topup_amount} onChange={e => setForm({...form, february_topup_amount: +e.target.value})} className={INP} /></Field>
              </>
            )}
          </div>
          <Field label="Note"><input value={form.note} onChange={e => setForm({...form, note: e.target.value})} className={INP} /></Field>
          <div className="flex gap-2">
            <button onClick={save} disabled={saving} className="px-4 py-1.5 bg-[#3b4872] text-white rounded-sm text-xs font-semibold" data-testid="save-pt-version-btn">
              {saving ? 'Saving…' : 'Create Version'}
            </button>
            <button onClick={() => setShowNew(false)} className="px-4 py-1.5 border border-slate-300 rounded-sm text-xs">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
